import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { currencyPricePrefix } from 'customer-data-objects/lineItem/PropertyNames';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { Select } from 'foundations-components/transitional/components/Select';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/transitional/components/View';
import _get from 'hs-lodash/get';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { COMMON_HTTP_MESSAGES, DEBOUNCE_TIME } from '../../../../constants/Http';
import { PRODUCT_REQUEST_PROPERTIES, PROPERTY_NAME_TO_REFERENCE_TYPE } from '../../../../constants/associations';
import { AssociationModalPropsContext } from '../../../../contexts/associationModal/PropsContext';
import { AssociationModalContext } from '../../../../contexts/associationModal/context';
import { PropertiesContext } from '../../../../contexts/properties/context';
import captureException from '../../../../utils/captureException';
import { generateManageProductsLink } from '../../../../utils/urls';
import { Container, DeleteIconWrapper } from './AssociationLineItem.style';
import { DeleteIcon } from 'foundations-assets/icons/components/DeleteIcon';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function AssociationLineItem({
  line,
  property,
  index,
  onLineItemChange,
  onQuantityChange,
  deleteLine,
  trackSelectOpen
}) {
  const {
    crmObjectType,
    apiWrapper,
    openUrl,
    portalId
  } = useContext(AssociationModalPropsContext);
  const [isHover, setIsHover] = useState(false);
  const [isError, setIsError] = useState(false);
  const userInputRef = useRef('');
  const {
    currency,
    hasMulticurrencyActive,
    associationModalActions,
    productEntities
  } = useContext(AssociationModalContext);
  const {
    externalOptions
  } = useContext(PropertiesContext);
  const externalCurrencies = useMemo(() => externalOptions[crmObjectType][PROPERTY_NAME_TO_REFERENCE_TYPE.deal.deal_currency_code], [externalOptions, crmObjectType]);
  const openUrlHandler = () => openUrl(generateManageProductsLink(portalId));
  const currencies = useMemo(() => {
    if (!externalCurrencies) return [];
    return _get(externalCurrencies, 'results', []).map(item => item.id.toLowerCase());
  }, [externalCurrencies]);
  const onInputChange = event => onQuantityChange({
    quantity: Number(event.target.value),
    index
  });
  const onOpenChangeHandler = () => {
    trackSelectOpen();
  };
  const deleteLineHandler = () => deleteLine(index);
  const mouseEnterHandler = () => setIsHover(true);
  const mouseLeaveHandler = () => setIsHover(false);
  const onChangeHandler = event => {
    const value = event.currentTarget.value.value;
    if (value) {
      onLineItemChange({
        line: {
          quantity: 1,
          id: value.toString()
        },
        index
      });
    }
    setIsHover(false);
  };
  const toSelectOptions = useCallback(data => {
    return data.map(item => {
      const currencyKey = hasMulticurrencyActive ? `${currencyPricePrefix}${currency.toLowerCase()}` : 'price';
      const isCurrencyAvailbale = !!item.properties[currencyKey];
      const period = item.properties.recurringbillingfrequency ? item.properties.recurringbillingfrequency.replace('per_', '').replace('_', ' ') : '';
      const getSupportText = () => {
        if (isCurrencyAvailbale) {
          return `${I18n.formatIntlCurrency(Number(item.properties[currencyKey]), {
            currencyCode: currency
          })}${period ? ` / ${period}` : ''}`;
        }
        return /*#__PURE__*/_jsx(StatusTag, {
          use: "warning",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "associationModals.lineItems.missingCurrency"
          })
        });
      };
      const result = {
        text: item.properties.name || '',
        value: item.id,
        disabled: !isCurrencyAvailbale,
        help: getSupportText()
      };
      return result;
    });
  }, [currency, hasMulticurrencyActive]);
  const loadOptionsHandler = useCallback((input, callback, pagination = {
    offset: 0
  }) => {
    const currenciesKeys = currencies.map(item => `${currencyPricePrefix}${item}`);
    apiWrapper.searchObject({
      objectType: 'products',
      query: input !== null && input !== void 0 ? input : '',
      after: pagination.offset.toString(),
      limit: 50,
      properties: [...PRODUCT_REQUEST_PROPERTIES, ...currenciesKeys]
    }, userInputRef.current !== input ? DEBOUNCE_TIME : 0).then(response => {
      const newOptions = toSelectOptions([...Object.values(productEntities), ...response.results]);
      const nextAfter = _get(response, 'paging.next.after', '');
      associationModalActions.saveProducts(response.results);
      callback(null, {
        options: newOptions,
        pagination: {
          hasMore: !!nextAfter,
          length: response.total,
          offset: nextAfter
        }
      });
    }).catch(error => {
      setIsError(true);
      if (!error.message.includes(COMMON_HTTP_MESSAGES.REQUEST_ABORTED)) {
        callback(error, {
          options: []
        });
      }
      captureException(error);
    });
    if (userInputRef.current !== input) {
      userInputRef.current = input;
    }
  }, [currencies, apiWrapper, toSelectOptions, productEntities, associationModalActions]);
  const filterOptionsHandler = () => toSelectOptions(Object.values(productEntities));
  return /*#__PURE__*/_jsx(Container, {
    children: /*#__PURE__*/_jsxs(TFlex, {
      onMouseEnter: mouseEnterHandler,
      onMouseLeave: mouseLeaveHandler,
      align: "center",
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 2,
        marginInlineEnd: "400",
        children: /*#__PURE__*/_jsx(Select, {
          error: isError,
          searchable: true,
          onSelectedOptionChange: onChangeHandler,
          placeholder: I18n.text('associationModals.search.searchLineItemPlaceholder', {
            objectType: (property.definition.toObjectType || '').split('_').join(' ').toLowerCase()
          }),
          onOpenChange: onOpenChangeHandler,
          value: line.id,
          "aria-label": "line-item",
          loadOptions: loadOptionsHandler,
          dropdownFooter: /*#__PURE__*/_jsxs(Button, {
            use: "link",
            onClick: openUrlHandler,
            children: [/*#__PURE__*/_jsx(View, {
              as: "span",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "associationsPanel.tabs.create.associationProperties.manageProducts"
              })
            }), /*#__PURE__*/_jsx(ExternalLinkIcon, {
              size: "400"
            })]
          }),
          filterOptions: filterOptionsHandler
        })
      }), /*#__PURE__*/_jsx(Box, {
        grow: 0,
        basis: 50,
        marginInlineEnd: "400",
        children: /*#__PURE__*/_jsx(NumberInput, {
          disabled: !line.id,
          value: line.quantity,
          onChange: onInputChange
        })
      }), /*#__PURE__*/_jsx(DeleteIconWrapper, {
        isHover: isHover,
        children: /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          onClick: deleteLineHandler,
          children: /*#__PURE__*/_jsx(DeleteIcon, {
            color: "fill-info-default",
            size: "400"
          })
        })
      })]
    })
  });
}
export default AssociationLineItem;